import {
  API_ROUTES,
  documentDetailSchema,
  documentDownloadTicketSchema,
  documentSummarySchema,
  inboxSummarySchema,
  intakeStatsSchema,
  type ListDocumentsQuery,
} from '@hvac/contracts';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

/**
 * Document queries.
 *
 * The list is an infinite query over a keyset cursor, not a page number.
 * Offset pagination silently skips or repeats rows when documents are inserted
 * mid-scroll, which is exactly what happens while a hundred-file upload is
 * running behind the list the user is reading.
 */

export const documentKeys = {
  all: (organizationId: string) => ['documents', organizationId] as const,
  list: (organizationId: string, filters: Partial<ListDocumentsQuery>) =>
    ['documents', organizationId, 'list', filters] as const,
  detail: (organizationId: string, documentId: string) =>
    ['documents', organizationId, 'detail', documentId] as const,
  stats: (organizationId: string, windowDays: number) =>
    ['documents', organizationId, 'stats', windowDays] as const,
} as const;

const documentPageSchema = z.object({
  items: z.array(documentSummarySchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

function toSearchParams(filters: Partial<ListDocumentsQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  return params.toString();
}

export const documentListQuery = (organizationId: string, filters: Partial<ListDocumentsQuery>) =>
  infiniteQueryOptions({
    queryKey: documentKeys.list(organizationId, filters),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => {
      const search = toSearchParams({
        ...filters,
        ...(pageParam === null ? {} : { cursor: pageParam }),
      });
      return apiClient.request({
        path: `${API_ROUTES.documents.list(organizationId)}${search === '' ? '' : `?${search}`}`,
        schema: documentPageSchema,
        signal,
      });
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    // Uploads land continuously while the list is open, so a short stale time
    // keeps it honest without a websocket.
    staleTime: 10_000,
  });

export const documentDetailQuery = (organizationId: string, documentId: string) =>
  queryOptions({
    queryKey: documentKeys.detail(organizationId, documentId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.documents.byId(organizationId, documentId),
        schema: documentDetailSchema,
        signal,
      }),
  });

export const intakeStatsQuery = (organizationId: string, windowDays = 30) =>
  queryOptions({
    queryKey: documentKeys.stats(organizationId, windowDays),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: `${API_ROUTES.intake.stats(organizationId)}?windowDays=${windowDays}`,
        schema: intakeStatsSchema,
        signal,
      }),
    staleTime: 60_000,
  });

/**
 * Mints a short-lived signed URL.
 *
 * Not a query: it has a side effect (it writes an audit row for originals) and
 * the result expires. Caching it would serve a dead link.
 */
export async function requestDownloadUrl(
  organizationId: string,
  documentId: string,
  options: { versionId?: string; preview?: boolean; expiresInSeconds?: number } = {},
) {
  return apiClient.request({
    method: 'POST',
    path: API_ROUTES.documents.download(organizationId, documentId),
    schema: documentDownloadTicketSchema,
    body: {
      ...(options.versionId === undefined ? {} : { versionId: options.versionId }),
      preview: options.preview ?? false,
      expiresInSeconds: options.expiresInSeconds ?? 300,
    },
  });
}

export async function retryDocument(organizationId: string, documentId: string): Promise<void> {
  await apiClient.request({
    method: 'POST',
    path: API_ROUTES.documents.retry(organizationId, documentId),
    schema: z.undefined(),
  });
}

export async function archiveDocument(
  organizationId: string,
  documentId: string,
  reason: string,
): Promise<void> {
  await apiClient.request({
    method: 'POST',
    path: API_ROUTES.documents.archive(organizationId, documentId),
    schema: z.undefined(),
    body: { reason },
  });
}

export const inboxSummaryQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['inbox', organizationId, 'summary'] as const,
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.intake.inboxSummary(organizationId),
        schema: inboxSummarySchema,
        signal,
      }),
    staleTime: 30_000,
  });
